export type ServicePath = 'ceramic' | 'detailing_quote'

export {
  ceramicPackages,
  coatingIncludes,
  formatCurrencyFromCents,
  getCeramicPackage,
  warrantyInformation,
  type CeramicPackage,
  type CurrencyCode,
  type SelectedProtection,
} from './protection-catalog'

export {
  NO_ADDITIONAL_PROTECTION_ID,
  ADD_ON_PRICE_NOTE,
  protectionAddOns,
  type ProtectionAddOn,
} from './protection-addons'

export interface DetailingServiceInterest {
  id: string
  name: string
  description?: string
  benefits?: string[]
  options?: string[]
}

export interface BookingDraft {
  servicePath: ServicePath
  packageId?: string
  customer: {
    name: string
    email: string
    phone: string
    preferredContactMethod?: string
  }
  vehicle: {
    type: string
    make: string
    model: string
    year: string
    colour: string
    registration?: string
  }
  addOns: string[]
  addOnsCommitted?: boolean
  noAdditionalProtection?: boolean
  preferredDate?: string
  preferredTime?: string
  notes?: string
  uploadedImages?: string[]
  startingPriceCents?: number
  depositAmountCents?: number
  publicReference?: string
  checkoutSessionId?: string
  requiresManualConfirmation: boolean
}

export { CERAMIC_BOOKING_DEPOSIT_CENTS } from '../lib/booking-domain'

export const vehicleTypeOptions = [
  'Car',
  'SUV / 4WD',
  'Ute',
  'Van / Commercial Vehicle',
  'Caravan',
  'Motorbike',
  'Other',
]

export const manualConfirmationVehicleTypes = new Set([
  'Van / Commercial Vehicle',
  'Caravan',
  'Motorbike',
  'Other',
])

export const scheduleTimes = ['08:00 AM', '10:30 AM', '01:00 PM', '03:30 PM']

export const detailingInterests: DetailingServiceInterest[] = [
  { id: 'exterior-detailing', name: 'Exterior Detailing' },
  { id: 'premium-detailing', name: 'Premium Detailing & Enhancement' },
  { id: 'interior-detailing', name: 'Interior detailing as part of a full enhancement' },
  { id: 'paint-correction', name: 'Paint Correction' },
  { id: 'paint-protection', name: 'Paint Protection' },
  { id: 'unsure', name: 'Not sure — recommend what my vehicle needs' },
]

export const detailingServices: DetailingServiceInterest[] = [
  {
    id: 'exterior-detailing',
    name: 'Exterior Detailing',
    description:
      'Restore your vehicle exterior with a thorough, safe and professional detail designed to remove dirt, contamination and environmental fallout while enhancing the overall appearance of your paintwork.',
    benefits: [
      'Safe hand wash',
      'Wheel and tyre cleaning',
      'Chemical decontamination',
      'Exterior trim cleaned',
      'Improved gloss and finish',
      'Prepared for paint protection or ceramic coating',
    ],
  },
  {
    id: 'premium-detailing',
    name: 'Premium Detailing & Enhancement',
    description:
      'A fuller detailing enhancement for vehicles that need more than an exterior refresh. Interior work is included as part of this service when the vehicle needs it, rather than as a standalone interior-only job.',
    benefits: [
      'Exterior wash and decontamination',
      'Interior refresh as part of the enhancement',
      'Carpets, trims and touchpoints detailed',
      'Finish prepared for protection where suitable',
    ],
  },
  {
    id: 'paint-protection',
    name: 'Paint Protection',
    description:
      'Protect your vehicle finish and make ongoing maintenance easier with professional paint protection. Whether your vehicle is brand new or has been on the road for years, protecting your paintwork helps maintain its appearance while making washing and maintenance easier.',
    options: [
      'GYEON Ceramic Coatings',
      'Glass Protection',
      'Wheel Protection',
      'Exterior Trim Protection',
      'Interior Protection',
    ],
  },
]

export const emptyBookingDraft: BookingDraft = {
  servicePath: 'ceramic',
  customer: {
    name: '',
    email: '',
    phone: '',
    preferredContactMethod: 'Phone',
  },
  vehicle: {
    type: 'Car',
    make: '',
    model: '',
    year: '',
    colour: '',
    registration: '',
  },
  addOns: [],
  addOnsCommitted: false,
  noAdditionalProtection: false,
  notes: '',
  requiresManualConfirmation: false,
}

export function getBookingDraft(): BookingDraft {
  if (typeof window === 'undefined') return emptyBookingDraft

  try {
    const stored = window.localStorage.getItem('autoglow.bookingDraft')
    if (!stored) return emptyBookingDraft
    return { ...emptyBookingDraft, ...JSON.parse(stored) } as BookingDraft
  } catch {
    return emptyBookingDraft
  }
}

export function saveBookingDraft(draft: BookingDraft) {
  if (typeof window === 'undefined') return
  window.localStorage.setItem('autoglow.bookingDraft', JSON.stringify(draft))
}
